import {
  Badge,
  Box,
  Button,
  HStack,
  Spinner,
  Text,
  VStack
} from "@chakra-ui/react"
import {
  GoogleMap,
  InfoWindow,
  Marker,
  useJsApiLoader
} from "@react-google-maps/api"
import type { FC } from "react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import {
  formatCurrency,
  formatPayback,
  formatRoi
} from "../../utils/formatters"
import StateBox from "../common/StateBox"
import { StreetViewImage } from "../common/StreetViewImage"

const GOOGLE_MAPS_LIBRARIES: ["places"] = ["places"]

const DEFAULT_CENTER = { lat: 54.5, lng: -3.2 }
const DEFAULT_ZOOM = 6

const PROPERTY_MAP_STYLES: google.maps.MapTypeStyle[] = [
  // Keep road/city labels visible while removing businesses, shops, and venues.
  { featureType: "poi", stylers: [{ visibility: "off" }] },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
  {
    featureType: "road",
    elementType: "labels.icon",
    stylers: [{ visibility: "off" }]
  }
]

const MAP_OPTIONS: google.maps.MapOptions = {
  disableDefaultUI: true,
  zoomControl: true,
  fullscreenControl: true,
  streetViewControl: false,
  mapTypeControl: false,
  clickableIcons: false,
  gestureHandling: "greedy",
  styles: PROPERTY_MAP_STYLES
}

type RoiLevel = "high" | "medium" | "low" | "unknown"

const ROI_MARKER_CONFIG: Record<RoiLevel, { label: string; color: string }> = {
  high: { label: "High", color: "#16A34A" },
  medium: { label: "Medium", color: "#F97316" },
  low: { label: "Low", color: "#DC2626" },
  unknown: { label: "Unknown", color: "#6B7280" }
}

interface MapViewport {
  center: { lat: number; lng: number }
  zoom: number
}

export interface PropertyMapBadge {
  label: string
  colorPalette?: string
}

export interface PropertyMapItem {
  id: string
  homeId: number
  opportunityId?: number
  address: string
  lat: number | null
  lng: number | null
  imageUrl?: string | null
  detailLine?: string
  badges?: PropertyMapBadge[]
  meta?: string[]
  roi?: number | null
  roiLabel?: string
  roiColor?: string
  upgradeCost?: string | number | null
  paybackPeriod?: number | null
  paybackColor?: string
  showMetrics?: boolean
}

interface PropertyMapViewProps {
  items: PropertyMapItem[]
  storageKey: string
  onViewDetails: (item: PropertyMapItem) => void
  ctaLabel?: string
  hasMore?: boolean
  isLoadingMore?: boolean
  onLoadMore?: () => void
}

const getRoiLevel = (roi: number | null | undefined): RoiLevel => {
  if (roi == null) return "unknown"
  if (roi >= 8) return "high"
  if (roi >= 4) return "medium"
  return "low"
}

export const toMapCoordinate = (
  value: string | number | null | undefined
): number | null => {
  if (value == null || value === "") return null
  const coordinate = typeof value === "number" ? value : Number(value)
  return Number.isFinite(coordinate) ? coordinate : null
}

const isValidViewport = (value: any): value is MapViewport =>
  typeof value?.center?.lat === "number" &&
  typeof value?.center?.lng === "number" &&
  Number.isFinite(value.center.lat) &&
  Number.isFinite(value.center.lng) &&
  typeof value?.zoom === "number" &&
  Number.isFinite(value.zoom)

const readViewport = (storageKey: string): MapViewport | null => {
  if (typeof window === "undefined") return null
  try {
    const raw = window.localStorage.getItem(storageKey)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    return isValidViewport(parsed) ? parsed : null
  } catch {
    return null
  }
}

const writeViewport = (storageKey: string, viewport: MapViewport) => {
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(viewport))
  } catch {
    // Ignore storage quota/privacy failures; the map remains fully usable.
  }
}

const createPinIcon = (color: string): google.maps.Icon | undefined => {
  if (typeof google === "undefined" || !google.maps) return undefined

  const svg = `
    <svg width="36" height="42" viewBox="0 0 36 42" xmlns="http://www.w3.org/2000/svg">
      <path d="M18 1.5C8.9 1.5 1.5 8.7 1.5 17.7C1.5 29 18 40.5 18 40.5C18 40.5 34.5 29 34.5 17.7C34.5 8.7 27.1 1.5 18 1.5Z" fill="${color}" stroke="white" stroke-width="3"/>
      <circle cx="18" cy="17.5" r="6.2" fill="white" fill-opacity="0.95"/>
    </svg>
  `

  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: new google.maps.Size(36, 42),
    anchor: new google.maps.Point(18, 42)
  }
}

const PreviewMetric: FC<{ label: string; value: string; color?: string }> = ({
  label,
  value,
  color = "gray.800"
}) => (
  <VStack align="start" gap={0} minW={0} flex={1}>
    <Text
      fontSize="10px"
      color="gray.400"
      fontWeight="medium"
      textTransform="uppercase"
      letterSpacing="0"
      lineHeight="1.2"
    >
      {label}
    </Text>
    <Text fontSize="sm" fontWeight="semibold" color={color} lineClamp={1}>
      {value}
    </Text>
  </VStack>
)

const PropertyPreview: FC<{
  item: PropertyMapItem
  ctaLabel: string
  onViewDetails: (item: PropertyMapItem) => void
}> = ({ item, ctaLabel, onViewDetails }) => {
  const roiLevel = getRoiLevel(item.roi)
  const roiColor = item.roiColor ?? ROI_MARKER_CONFIG[roiLevel].color

  return (
    <Box
      className="gf-map-preview"
      w="288px"
      maxW="calc(100vw - 72px)"
      bg="white"
      overflow="hidden"
      color="gray.800"
      fontFamily='"Lexend", sans-serif'
    >
      <Box px={3} pt={3}>
        <StreetViewImage
          imageUrl={item.imageUrl}
          address={item.address}
          svSize="400x260"
          alt={item.address}
          w="100%"
          h="108px"
          borderRadius="md"
          border="2px solid"
          borderColor="green.700"
          objectFit="cover"
        />
      </Box>
      <VStack align="stretch" gap={3} p={3}>
        <VStack align="start" gap={1}>
          <Text
            fontSize="sm"
            fontWeight="semibold"
            color="gray.900"
            lineClamp={2}
          >
            {item.address}
          </Text>
          {item.detailLine && (
            <Text fontSize="xs" color="gray.500" lineClamp={1}>
              {item.detailLine}
            </Text>
          )}
          {(item.badges?.length || item.meta?.length) && (
            <HStack gap={2} flexWrap="wrap" mt={1}>
              {item.badges?.map((badge) => (
                <Badge
                  key={badge.label}
                  colorPalette={badge.colorPalette ?? "gray"}
                  variant="subtle"
                  fontSize="10px"
                >
                  {badge.label}
                </Badge>
              ))}
              {item.meta?.map((meta) => (
                <Text
                  key={meta}
                  fontSize="xs"
                  color="gray.500"
                  fontWeight="medium"
                >
                  {meta}
                </Text>
              ))}
            </HStack>
          )}
        </VStack>

        {item.showMetrics !== false && (
          <HStack align="start" gap={3}>
            <PreviewMetric
              label={item.roiLabel ?? "Green ROI"}
              value={formatRoi(item.roi)}
              color={roiColor}
            />
            <PreviewMetric
              label="Upgrade Cost"
              value={formatCurrency(item.upgradeCost)}
            />
            <PreviewMetric
              label="Payback"
              value={formatPayback(item.paybackPeriod)}
              color={item.paybackColor}
            />
          </HStack>
        )}

        <Button
          size="sm"
          bg="green.800"
          color="white"
          _hover={{ bg: "green.700" }}
          alignSelf="stretch"
          onClick={() => onViewDetails(item)}
        >
          {ctaLabel}
        </Button>
      </VStack>
    </Box>
  )
}

const PropertyMapView: FC<PropertyMapViewProps> = ({
  items,
  storageKey,
  onViewDetails,
  ctaLabel = "View details",
  hasMore = false,
  isLoadingMore = false,
  onLoadMore
}) => {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ""
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: apiKey,
    libraries: GOOGLE_MAPS_LIBRARIES
  })

  const [map, setMap] = useState<google.maps.Map | null>(null)
  const [viewport, setViewport] = useState<MapViewport | null>(() =>
    readViewport(storageKey)
  )
  const [selectedItem, setSelectedItem] = useState<PropertyMapItem | null>(null)
  const hasFittedInitialBoundsRef = useRef(false)

  const mappableItems = useMemo(
    () => items.filter((item) => item.lat != null && item.lng != null),
    [items]
  )

  useEffect(() => {
    setViewport(readViewport(storageKey))
    setSelectedItem(null)
    hasFittedInitialBoundsRef.current = false
  }, [storageKey])

  useEffect(() => {
    if (!selectedItem) return
    const updatedSelectedItem = mappableItems.find((item) => item.id === selectedItem.id)
    if (!updatedSelectedItem) {
      setSelectedItem(null)
      return
    }
    if (updatedSelectedItem !== selectedItem) {
      setSelectedItem(updatedSelectedItem)
    }
  }, [mappableItems, selectedItem])

  const fallbackViewport = useMemo<MapViewport>(() => {
    if (mappableItems.length === 1) {
      return {
        center: { lat: mappableItems[0].lat!, lng: mappableItems[0].lng! },
        zoom: 13
      }
    }

    if (mappableItems.length > 1) {
      const center = mappableItems.reduce(
        (sum, item) => ({
          lat: sum.lat + item.lat!,
          lng: sum.lng + item.lng!
        }),
        { lat: 0, lng: 0 }
      )
      return {
        center: {
          lat: center.lat / mappableItems.length,
          lng: center.lng / mappableItems.length
        },
        zoom: DEFAULT_ZOOM
      }
    }

    return { center: DEFAULT_CENTER, zoom: DEFAULT_ZOOM }
  }, [mappableItems])

  const fitMapToItems = useCallback(
    (targetMap: google.maps.Map) => {
      if (mappableItems.length === 0) return

      if (mappableItems.length === 1) {
        targetMap.setCenter({
          lat: mappableItems[0].lat!,
          lng: mappableItems[0].lng!
        })
        targetMap.setZoom(13)
        return
      }

      const bounds = new google.maps.LatLngBounds()
      mappableItems.forEach((item) => {
        bounds.extend({ lat: item.lat!, lng: item.lng! })
      })
      targetMap.fitBounds(bounds, 48)
    },
    [mappableItems]
  )

  useEffect(() => {
    if (!map || viewport || hasFittedInitialBoundsRef.current) return
    fitMapToItems(map)
    hasFittedInitialBoundsRef.current = true
  }, [fitMapToItems, map, viewport])

  const handleIdle = useCallback(() => {
    if (!map) return
    const center = map.getCenter()
    const zoom = map.getZoom()
    if (!center || zoom == null) return

    const nextViewport = {
      center: { lat: center.lat(), lng: center.lng() },
      zoom
    }
    setViewport(nextViewport)
    writeViewport(storageKey, nextViewport)
  }, [map, storageKey])

  const markerIcons = useMemo(() => {
    if (!isLoaded) return null
    return {
      high: createPinIcon(ROI_MARKER_CONFIG.high.color),
      medium: createPinIcon(ROI_MARKER_CONFIG.medium.color),
      low: createPinIcon(ROI_MARKER_CONFIG.low.color),
      unknown: createPinIcon(ROI_MARKER_CONFIG.unknown.color)
    }
  }, [isLoaded])

  const infoWindowOptions = useMemo<
    google.maps.InfoWindowOptions | undefined
  >(() => {
    if (!isLoaded || typeof google === "undefined" || !google.maps)
      return undefined
    return {
      maxWidth: 320,
      pixelOffset: new google.maps.Size(0, -36)
    }
  }, [isLoaded])

  if (!apiKey) {
    return (
      <StateBox>
        <Text color="gray.600">Map view requires a Google Maps API key.</Text>
      </StateBox>
    )
  }

  if (!isLoaded) {
    return (
      <StateBox>
        <Spinner size="lg" color="green.400" />
        <Text mt={4} color="gray.500">
          Loading map...
        </Text>
      </StateBox>
    )
  }

  const mapCenter = viewport?.center ?? fallbackViewport.center
  const mapZoom = viewport?.zoom ?? fallbackViewport.zoom
  const mapHeight = {
    base: "clamp(480px, calc(100dvh - 210px), 780px)",
    lg: "clamp(640px, calc(100dvh - 220px), 1100px)"
  }

  return (
    <Box
      border="1px solid"
      borderColor="gray.200"
      borderRadius="md"
      overflow="hidden"
      bg="white"
      position="relative"
      h={mapHeight}
      minH={{ base: "480px", lg: "640px" }}
    >
      <Box h="100%" w="100%">
        <GoogleMap
          mapContainerStyle={{ width: "100%", height: "100%" }}
          center={mapCenter}
          zoom={mapZoom}
          onLoad={(loadedMap) => setMap(loadedMap)}
          onUnmount={() => setMap(null)}
          onIdle={handleIdle}
          options={MAP_OPTIONS}
        >
          {mappableItems.map((item) => {
            const roiLevel = getRoiLevel(item.roi)
            return (
              <Marker
                key={item.id}
                position={{ lat: item.lat!, lng: item.lng! }}
                icon={markerIcons?.[roiLevel]}
                zIndex={
                  selectedItem?.id === item.id
                    ? 20
                    : roiLevel === "high"
                      ? 10
                      : 1
                }
                onClick={() => {
                  const position = { lat: item.lat!, lng: item.lng! }
                  map?.panTo(position)
                  setSelectedItem(item)
                }}
              />
            )
          })}

          {selectedItem &&
            selectedItem.lat != null &&
            selectedItem.lng != null && (
              <InfoWindow
                position={{ lat: selectedItem.lat, lng: selectedItem.lng }}
                onCloseClick={() => setSelectedItem(null)}
                options={infoWindowOptions}
              >
                <PropertyPreview
                  item={selectedItem}
                  ctaLabel={ctaLabel}
                  onViewDetails={onViewDetails}
                />
              </InfoWindow>
            )}
        </GoogleMap>
      </Box>

      <HStack
        position="absolute"
        top={3}
        left={3}
        gap={3}
        flexWrap="wrap"
        bg="white"
        border="1px solid"
        borderColor="gray.200"
        borderRadius="md"
        px={3}
        py={2}
        boxShadow="sm"
      >
        {(Object.keys(ROI_MARKER_CONFIG) as RoiLevel[]).map((level) => (
          <HStack key={level} gap={1.5}>
            <Box
              w="10px"
              h="10px"
              borderRadius="full"
              bg={ROI_MARKER_CONFIG[level].color}
              flexShrink={0}
            />
            <Text fontSize="xs" color="gray.600" fontWeight="medium">
              {ROI_MARKER_CONFIG[level].label}
            </Text>
          </HStack>
        ))}
      </HStack>

      {items.length > 0 && mappableItems.length === 0 && (
        <Box
          position="absolute"
          left="50%"
          top="50%"
          transform="translate(-50%, -50%)"
          bg="white"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="md"
          px={4}
          py={3}
          boxShadow="sm"
        >
          <Text fontSize="sm" color="gray.600" fontWeight="medium">
            No map pins available for these results.
          </Text>
        </Box>
      )}

      {items.length > mappableItems.length && mappableItems.length > 0 && (
        <Box
          position="absolute"
          right={3}
          top={3}
          bg="white"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="md"
          px={3}
          py={2}
          boxShadow="sm"
        >
          <Text fontSize="xs" color="gray.600" fontWeight="medium">
            {mappableItems.length} of {items.length} shown
          </Text>
        </Box>
      )}

      {hasMore && onLoadMore && (
        <Button
          position="absolute"
          bottom={4}
          left="50%"
          transform="translateX(-50%)"
          size="sm"
          bg="white"
          color="green.800"
          border="1px solid"
          borderColor="gray.200"
          boxShadow="sm"
          loading={isLoadingMore}
          onClick={onLoadMore}
          _hover={{ bg: "gray.50" }}
        >
          Load more pins
        </Button>
      )}
    </Box>
  )
}

export default PropertyMapView
